// components/Admin/MiniroomManager/MiniroomFurnitureFormModal.jsx

import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, FormControlLabel, Checkbox
} from "@mui/material";
import api from "../../../api/axios";



export default function MiniroomFurnitureFormModal({ open, onClose, onSuccess }) {
    const [name, setName] = useState("");
    const [sprite, setSprite] = useState(null);        // 앞(front) 업로드
    const [spriteBack, setSpriteBack] = useState(null);     // 등(back) 업로드 
    const [sizeW, setSizeW] = useState(1);
    const [sizeH, setSizeH] = useState(1);
    const [anchorX, setAnchorX] = useState(0);
    const [anchorY, setAnchorY] = useState(0);
    const [rotatable, setRotatable] = useState(true);
    const [category, setCategory] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [price, setPrice] = useState(0);
    const [isUnlimited, setIsUnlimited] = useState(true);
    const [stock, setStock] = useState(0);
    const [previewFront, setPreviewFront] = useState(null);
    const [previewBack, setPreviewBack] = useState(null);
    const toInt = (v, def = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? Math.trunc(n) : def;
    };
    const nonneg = (v) => Math.max(0, toInt(v, 0));
    const pickImage = (file, setter) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            alert("이미지 파일만 업로드할 수 있습니다.");
            return;
        }
        setter(file);
    };


    // 2) 파일 바뀔 때 미리보기 URL 생성/해제
    useEffect(() => {
        if (!sprite) { setPreviewFront(null); return; }
        const url = URL.createObjectURL(sprite);
        setPreviewFront(url);
        return () => URL.revokeObjectURL(url);
    }, [sprite]);

    useEffect(() => {
        if (!spriteBack) { setPreviewBack(null); return; }
        const url = URL.createObjectURL(spriteBack);
        setPreviewBack(url);
        return () => URL.revokeObjectURL(url);
    }, [spriteBack]);

    // ✅ 무제한 체크되면 재고 자동 0 처리
    useEffect(() => {
        if (isUnlimited) setStock(0);
    }, [isUnlimited]);

    const reset = () => {
        setName(""); setSprite(null); setSpriteBack(null);
        setSizeW(1); setSizeH(1); setAnchorX(0); setAnchorY(0);
        setRotatable(true); setCategory(""); setIsActive(true);
        setPrice(0); setIsUnlimited(true); setStock(0);
        setPreviewFront(null); setPreviewBack(null);
    };

    const handleSubmit = async () => {
        const fd = new FormData();
        fd.append("name", name);
        if (sprite) fd.append("sprite", sprite);
        if (spriteBack) fd.append("sprite_back", spriteBack);
        fd.append("size_w", Math.max(1, toInt(sizeW, 1)));
        fd.append("size_h", Math.max(1, toInt(sizeH, 1)));
        fd.append("anchor_x", toInt(anchorX, 0));
        fd.append("anchor_y", toInt(anchorY, 0));
        fd.append("price", nonneg(price));
        fd.append("stock", isUnlimited ? 0 : nonneg(stock));
        fd.append("layer", "object");
        fd.append("rotatable", rotatable ? "true" : "false");
        fd.append("category", category);
        fd.append("is_active", isActive ? "true" : "false");
        fd.append("is_unlimited", isUnlimited ? "true" : "false");


        try {
            await api.post("/api/miniroom/admin/furnitures/", fd, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            onSuccess?.();
            reset();
            onClose();
        } catch (e) {
            const data = e?.response?.data;
            const detail = typeof data === "string"
                ? data
                : (data ? JSON.stringify(data, null, 2) : e.message);
            console.error(data || e);
            alert(`등록 실패\n${detail}`);
        }
    };

    const disabled =
        !name.trim() ||
        !sprite ||
        toInt(sizeW, 0) < 1 ||
        toInt(sizeH, 0) < 1 ||
        nonneg(price) < 0 ||
        (!isUnlimited && nonneg(stock) <= 0);

    return (
        <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>가구 등록</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={12}>
                        <TextField label="이름" value={name} onChange={e => setName(e.target.value)} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth>
                            앞(front) 이미지 업로드(필수)
                            <input hidden type="file" accept="image/*"
                                onChange={e => pickImage(e.target.files[0], setSprite)} />
                        </Button>

                        {/* 앞 미리보기 */}
                        <div style={{
                            marginTop: 8, border: "1px solid #e0e0e0", borderRadius: 8,
                            height: 120, display: "flex", alignItems: "center", justifyContent: "center",
                            background: "#fafafa"
                        }}>
                            {previewFront ? (
                                <img
                                    src={previewFront}
                                    alt="front preview"
                                    style={{ maxWidth: "100%", maxHeight: "100%", imageRendering: "pixelated" }}
                                    draggable={false}
                                />
                            ) : (
                                <span style={{ color: "#999", fontSize: 12 }}>미리보기 없음</span>
                            )}
                        </div>
                    </Grid>

                    {/* 등(back) 이미지 */}
                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth>
                            등(back) 이미지 업로드(선택)
                            <input hidden type="file" accept="image/*"
                                onChange={e => pickImage(e.target.files[0], setSpriteBack)} />
                        </Button>

                        {/* 등 미리보기 */}
                        <div style={{
                            marginTop: 8, border: "1px solid #e0e0e0", borderRadius: 8,
                            height: 120, display: "flex", alignItems: "center", justifyContent: "center",
                            background: "#fafafa"
                        }}>
                            {previewBack ? (
                                <img
                                    src={previewBack}
                                    alt="back preview"
                                    style={{ maxWidth: "100%", maxHeight: "100%", imageRendering: "pixelated" }}
                                    draggable={false}
                                />
                            ) : (
                                <span style={{ color: "#999", fontSize: 12 }}>미리보기 없음</span>
                            )}
                        </div>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                        <TextField label="가로칸(size_w)" type="number" value={sizeW} onChange={e => setSizeW(Math.max(1, toInt(e.target.value, 1)))} fullWidth />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField label="세로칸(size_h)" type="number" value={sizeH} onChange={e => setSizeH(Math.max(1, toInt(e.target.value, 1)))} fullWidth />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField label="앵커X" type="number" value={anchorX} onChange={e => setAnchorX(toInt(e.target.value, 0))} fullWidth />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField label="앵커Y" type="number" value={anchorY} onChange={e => setAnchorY(toInt(e.target.value, 0))} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField label="카테고리" value={category} onChange={e => setCategory(e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="가격(Leaf)"
                            type="number"
                            value={price} onChange={e => setPrice(nonneg(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox checked={rotatable} onChange={e => setRotatable(e.target.checked)} />}
                            label="회전 가능"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
                            label="카탈로그 노출"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox checked={isUnlimited} onChange={e => setIsUnlimited(e.target.checked)} />}
                            label="무제한 판매"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="재고"
                            type="number"
                            value={stock} onChange={e => setStock(nonneg(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                            disabled={isUnlimited}
                            helperText={isUnlimited ? "무제한 판매에서는 재고가 사용되지 않습니다." : ""}
                        />
                    </Grid>


                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { reset(); onClose(); }}>취소</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={disabled}>등록</Button>
            </DialogActions>
        </Dialog>
    );
}
